import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  Calendar,
  Filter,
  Download,
  Search,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  ChevronDown,
  MoreVertical,
  BarChart3,
  ShoppingCart,
  RefreshCw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import Card from '../../../components/ui/Card';
import DataTable from '../../../components/ui/DataTable';
import Badge from '../../../components/ui/Badge';
import Select from '../../../components/common/Select';
import Modal from '../../../components/common/Modal';
import { useSales } from '../../../hooks/useSales';

const SalesPage = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [filteredSales, setFilteredSales] = useState([]);
  
  const { 
    sales = [], 
    loading, 
    stats = {},
    exportSales,
    refreshSales
  } = useSales();

  // Safe stats with defaults
  const safeStats = {
    totalRevenue: stats?.totalRevenue || 0,
    totalOrders: stats?.totalOrders || 0,
    avgOrderValue: stats?.avgOrderValue || 0,
    conversionRate: stats?.conversionRate || 0,
    pendingOrders: stats?.pendingOrders || 0,
    completedOrders: stats?.completedOrders || 0,
    revenueChange: stats?.revenueChange || 0,
    ordersChange: stats?.ordersChange || 0
  };

  // Safe sales array
  const safeSales = Array.isArray(sales) ? sales : [];

  // Apply filters
  useEffect(() => {
    let filtered = [...safeSales];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        order?.id?.toLowerCase().includes(query) ||
        order?.customer?.companyName?.toLowerCase().includes(query) ||
        order?.customer?.email?.toLowerCase().includes(query) ||
        order?.shippingAddress?.city?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order?.status === statusFilter);
    }

    // Apply payment filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(order => order?.paymentStatus === paymentFilter);
    }

    setFilteredSales(filtered);
  }, [safeSales, searchQuery, statusFilter, paymentFilter]);

  // Get top products safely
  const getTopProducts = () => {
    const productMap = new Map();
    
    safeSales.forEach(order => {
      const items = order?.items || [];
      items.forEach(item => {
        if (item?.product) {
          const productId = item.product.id || item.product._id;
          const productName = item.product.name || 'Unnamed Product';
          const quantity = item.quantity || 0;
          const price = item.price || 0;
          const image = item.product.imageUrl || '/api/placeholder/40/40';
          
          if (productMap.has(productId)) {
            const existing = productMap.get(productId);
            productMap.set(productId, {
              ...existing,
              quantity: existing.quantity + quantity,
              revenue: existing.revenue + (quantity * price)
            });
          } else {
            productMap.set(productId, {
              id: productId,
              name: productName,
              image: image,
              quantity: quantity,
              price: price,
              revenue: quantity * price
            });
          }
        }
      });
    });
    
    // Convert to array and sort by revenue
    return Array.from(productMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const topProducts = getTopProducts();

  const columns = [
    {
      id: 'select',
      header: '',
      render: (item) => (
        <input
          type="checkbox"
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          onChange={() => {}}
        />
      ),
      width: '40px'
    },
    {
      id: 'orderId',
      header: 'Order ID',
      accessor: 'id',
      render: (item) => {
        const orderId = item?.id || '';
        const shortId = orderId.slice(0, 8).toUpperCase();
        return (
          <div className="flex items-center">
            <ShoppingCart className="h-4 w-4 text-gray-400 mr-2" />
            <Link 
              to={`/seller/orders/${orderId}`} 
              className="text-blue-600 hover:underline font-medium text-sm"
            >
              #{shortId}
            </Link>
          </div>
        );
      }
    },
    {
      id: 'customer',
      header: 'Customer',
      render: (item) => {
        const customer = item?.customer || {};
        return (
          <div>
            <div className="font-medium text-gray-900 text-sm">
              {customer.companyName || 'Individual Customer'}
            </div>
            <div className="text-xs text-gray-500 truncate max-w-[150px]">
              {customer.email || 'N/A'}
            </div>
          </div>
        );
      }
    },
    {
      id: 'products',
      header: 'Products',
      render: (item) => {
        const items = item?.items || [];
        return (
          <div className="flex items-center">
            <span className="font-medium mr-2">{items.length}</span>
            <Package className="h-4 w-4 text-gray-400" />
          </div>
        );
      }
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: 'total',
      render: (item) => (
        <div className="font-bold text-gray-900">
          ${(item?.total || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </div>
      )
    },
    {
      id: 'status',
      header: 'Status',
      render: (item) => {
        const status = item?.status || 'pending';
        const statusConfig = {
          pending: { label: 'Pending', color: 'yellow', icon: Clock },
          processing: { label: 'Processing', color: 'blue', icon: AlertCircle },
          shipped: { label: 'Shipped', color: 'purple', icon: Package },
          delivered: { label: 'Delivered', color: 'green', icon: CheckCircle },
          cancelled: { label: 'Cancelled', color: 'red', icon: XCircle },
          completed: { label: 'Completed', color: 'green', icon: CheckCircle }
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        const Icon = config.icon;
        
        return (
          <Badge variant={config.color} className="inline-flex items-center gap-1 text-xs">
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    },
    {
      id: 'payment',
      header: 'Payment',
      render: (item) => {
        const status = item?.paymentStatus || 'pending';
        const isPaid = status === 'paid';
        
        return (
          <Badge 
            variant={isPaid ? 'success' : 'warning'} 
            className="text-xs"
          >
            {isPaid ? 'Paid' : 'Pending'}
          </Badge>
        );
      }
    },
    {
      id: 'date',
      header: 'Date',
      accessor: 'createdAt',
      render: (item) => {
        const date = item?.createdAt;
        if (!date) return 'N/A';
        
        const dateObj = new Date(date);
        return (
          <div className="text-sm">
            <div className="text-gray-900">{dateObj.toLocaleDateString()}</div>
            <div className="text-gray-500 text-xs">{dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (item) => {
        const orderId = item?.id;
        if (!orderId) return null;
        
        return (
          <div className="flex items-center space-x-1">
            <Button
              size="small"
              variant="ghost"
              onClick={() => {
                setSelectedOrder(item);
                setShowOrderDetails(true);
              }}
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="small"
              variant="ghost"
              onClick={() => window.location.href = `/seller/orders/${orderId}/edit`}
              title="Edit Order"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </div>
        );
      }
    }
  ];

  const salesStats = [
    {
      title: 'Total Revenue',
      value: `$${safeStats.totalRevenue.toLocaleString()}`,
      change: safeStats.revenueChange,
      trend: safeStats.revenueChange >= 0 ? 'up' : 'down',
      icon: DollarSign,
      color: 'green',
      description: 'Revenue this month'
    },
    {
      title: 'Total Orders',
      value: safeStats.totalOrders.toLocaleString(),
      change: safeStats.ordersChange,
      trend: safeStats.ordersChange >= 0 ? 'up' : 'down',
      icon: Package,
      color: 'blue',
      description: 'Orders this month'
    },
    {
      title: 'Avg. Order Value',
      value: `$${safeStats.avgOrderValue.toFixed(2)}`,
      change: '+5.2%',
      trend: 'up',
      icon: TrendingUp,
      color: 'purple',
      description: 'Average order value'
    },
    {
      title: 'Completed Orders',
      value: safeStats.completedOrders.toLocaleString(),
      change: '+12.5%',
      trend: 'up',
      icon: CheckCircle,
      color: 'green',
      description: 'Orders delivered'
    },
    {
      title: 'Pending Orders',
      value: safeStats.pendingOrders.toLocaleString(),
      change: '-3.2%',
      trend: 'down',
      icon: Clock,
      color: 'yellow',
      description: 'Orders pending'
    },
    {
      title: 'Conversion Rate',
      value: `${safeStats.conversionRate}%`,
      change: '+2.1%',
      trend: 'up',
      icon: Users,
      color: 'orange',
      description: 'Visit to order ratio'
    }
  ];

  const timeRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by useEffect
  };

  const handleExport = () => {
    if (exportSales) {
      exportSales({
        timeRange,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        paymentStatus: paymentFilter !== 'all' ? paymentFilter : undefined,
        searchQuery: searchQuery.trim() || undefined
      });
    }
  };

  const handleRefresh = () => {
    if (refreshSales) {
      refreshSales();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sales data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Dashboard</h1>
          <p className="text-gray-600">Monitor your sales performance and orders</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="small"
            onClick={handleRefresh}
            disabled={!refreshSales}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="small"
            onClick={handleExport}
            disabled={!exportSales || filteredSales.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-2">
          {timeRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => setTimeRange(range.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range.value
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        <div className="text-sm text-gray-500">
          <Calendar className="h-4 w-4 inline mr-1" />
          Data for {timeRanges.find(r => r.value === timeRange)?.label}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {salesStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${
                  stat.color === 'green' ? 'bg-green-100' :
                  stat.color === 'blue' ? 'bg-blue-100' :
                  stat.color === 'purple' ? 'bg-purple-100' :
                  stat.color === 'yellow' ? 'bg-yellow-100' :
                  'bg-orange-100'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    stat.color === 'green' ? 'text-green-600' :
                    stat.color === 'blue' ? 'text-blue-600' :
                    stat.color === 'purple' ? 'text-purple-600' :
                    stat.color === 'yellow' ? 'text-yellow-600' :
                    'text-orange-600'
                  }`} />
                </div>
                <div className={`flex items-center text-xs font-medium px-2 py-1 rounded-full ${
                  stat.trend === 'up' 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {stat.trend === 'up' ? (
                    <ArrowUp className="h-3 w-3 mr-1" />
                  ) : (
                    <ArrowDown className="h-3 w-3 mr-1" />
                  )}
                  {stat.change}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-sm font-medium text-gray-900">{stat.title}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
            </Card>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        {/* Search */}
        <div className="flex-1 w-full lg:w-auto">
          <form onSubmit={handleSearch} className="w-full">
            <Input
              placeholder="Search orders by ID, customer, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="h-4 w-4 text-gray-400" />}
              className="w-full"
              size="small"
            />
          </form>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          <div className="flex-1 min-w-[150px]">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'processing', label: 'Processing' },
                { value: 'shipped', label: 'Shipped' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' }
              ]}
              size="small"
              fullWidth
            />
          </div>
          
          <div className="flex-1 min-w-[150px]">
            <Select
              value={paymentFilter}
              onChange={setPaymentFilter}
              options={[
                { value: 'all', label: 'All Payments' },
                { value: 'paid', label: 'Paid' },
                { value: 'pending', label: 'Pending Payment' },
                { value: 'failed', label: 'Failed' }
              ]}
              size="small"
              fullWidth
            />
          </div>

          <Button
            variant="outline"
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            className="whitespace-nowrap"
          >
            <Filter className="h-4 w-4 mr-2" />
            More Filters
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Additional Filters Panel */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option>Last 30 days</option>
                <option>Last 90 days</option>
                <option>This year</option>
                <option>Custom range</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min. Amount ($)
              </label>
              <Input
                type="number"
                placeholder="0"
                size="small"
                className="w-full"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max. Amount ($)
              </label>
              <Input
                type="number"
                placeholder="10000"
                size="small"
                className="w-full"
                min="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option>Newest First</option>
                <option>Oldest First</option>
                <option>Highest Amount</option>
                <option>Lowest Amount</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <Button
              variant="outline"
              size="small"
              onClick={() => {
                setStatusFilter('all');
                setPaymentFilter('all');
                setSearchQuery('');
                setShowFilters(false);
              }}
            >
              Clear All
            </Button>
            <Button size="small" onClick={() => setShowFilters(false)}>
              Apply Filters
            </Button>
          </div>
        </Card>
      )}

      {/* Main Content - Orders Table and Top Products Side by Side */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Orders Table - Takes 2/3 on desktop */}
        <div className="lg:col-span-2">
          <Card>
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
                  <p className="text-sm text-gray-600">
                    {filteredSales.length} order{filteredSales.length !== 1 ? 's' : ''} found
                    {searchQuery && ` for "${searchQuery}"`}
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  Updated just now
                </div>
              </div>
            </div>
            {filteredSales.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery || statusFilter !== 'all' || paymentFilter !== 'all' 
                    ? 'No orders match your criteria' 
                    : 'No orders yet'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || statusFilter !== 'all' || paymentFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Start selling to see your orders here'}
                </p>
                {(searchQuery || statusFilter !== 'all' || paymentFilter !== 'all') && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setPaymentFilter('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <DataTable
                  columns={columns}
                  data={filteredSales}
                  loading={loading}
                  pagination={{
                    page: 1,
                    limit: 10,
                    total: filteredSales.length,
                    pages: Math.ceil(filteredSales.length / 10)
                  }}
                  selection={true}
                  compact={true}
                  className="min-w-full"
                />
              </div>
            )}
          </Card>
        </div>

        {/* Top Products - Takes 1/3 on desktop */}
        <div className="space-y-6">
          {/* Top Products Card */}
          <Card>
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Top Products</h3>
              <p className="text-sm text-gray-600">Best selling items by revenue</p>
            </div>
            {topProducts.length === 0 ? (
              <div className="p-6 text-center">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No sales data yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {topProducts.map((product, index) => (
                  <div key={product.id || index} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-10 w-10 rounded object-cover"
                            onError={(e) => {
                              e.target.src = '/api/placeholder/40/40';
                            }}
                          />
                          <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                            {index + 1}
                          </div>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                            {product.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {product.quantity} sold
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">
                          ${product.revenue.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          ${product.price.toFixed(2)} each
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>Revenue</span>
                      <span className="font-medium">{((product.revenue / safeStats.totalRevenue) * 100).toFixed(1)}% of total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Quick Stats Card */}
          <Card>
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Avg. Items per Order</span>
                <span className="font-medium">{(filteredSales.reduce((acc, order) => acc + (order?.items?.length || 0), 0) / (filteredSales.length || 1)).toFixed(1)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Order Completion Rate</span>
                <span className="font-medium text-green-600">
                  {filteredSales.length > 0 
                    ? `${((filteredSales.filter(o => o?.status === 'completed' || o?.status === 'delivered').length / filteredSales.length) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Payment Success Rate</span>
                <span className="font-medium text-green-600">
                  {filteredSales.length > 0 
                    ? `${((filteredSales.filter(o => o?.paymentStatus === 'paid').length / filteredSales.length) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Avg. Processing Time</span>
                <span className="font-medium">2.5 days</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Detailed Analytics</h4>
              <p className="text-xs text-gray-600">Advanced sales reports</p>
            </div>
          </div>
          <Link to="/seller/analytics/sales">
            <Button variant="outline" size="small" fullWidth>
              View Reports
            </Button>
          </Link>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Performance</h4>
              <p className="text-xs text-gray-600">Track KPIs & metrics</p>
            </div>
          </div>
          <Link to="/seller/analytics/performance">
            <Button variant="outline" size="small" fullWidth>
              View Metrics
            </Button>
          </Link>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Customer Insights</h4>
              <p className="text-xs text-gray-600">Customer behavior</p>
            </div>
          </div>
          <Link to="/seller/customers">
            <Button variant="outline" size="small" fullWidth>
              View Customers
            </Button>
          </Link>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center mb-3">
            <div className="p-2 bg-orange-100 rounded-lg mr-3">
              <Download className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Export Data</h4>
              <p className="text-xs text-gray-600">Download all sales data</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="small" 
            fullWidth
            onClick={handleExport}
            disabled={!exportSales}
          >
            Export All
          </Button>
        </Card>
      </div>

      {/* Order Details Modal */}
      <Modal
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrder(null);
        }}
        title="Order Details"
        size="lg"
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Order ID</h4>
                <p className="font-mono text-sm">{selectedOrder.id}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Date</h4>
                <p>{new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Status</h4>
                <Badge variant={
                  selectedOrder.status === 'completed' || selectedOrder.status === 'delivered' ? 'success' :
                  selectedOrder.status === 'pending' ? 'warning' :
                  selectedOrder.status === 'cancelled' ? 'danger' : 'secondary'
                }>
                  {selectedOrder.status}
                </Badge>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Payment</h4>
                <Badge variant={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'}>
                  {selectedOrder.paymentStatus}
                </Badge>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Customer</h4>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedOrder.customer?.companyName || 'Individual Customer'}</p>
                <p className="text-sm text-gray-600">{selectedOrder.customer?.email}</p>
                {selectedOrder.shippingAddress && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.country}
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Items</h4>
              <div className="space-y-2">
                {selectedOrder.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <img
                        src={item.product?.imageUrl || '/api/placeholder/40/40'}
                        alt={item.product?.name}
                        className="h-8 w-8 rounded mr-3"
                      />
                      <div>
                        <p className="text-sm font-medium">{item.product?.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                    <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Amount</span>
                <span className="text-2xl font-bold">${selectedOrder.total?.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowOrderDetails(false)}
              >
                Close
              </Button>
              <Link to={`/seller/orders/${selectedOrder.id}`}>
                <Button>
                  View Full Details
                </Button>
              </Link>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalesPage;